import { mediaBoxStyle, videoPlayback, type BodyPart } from "@/lib/activityAnatomy";
import { upgradeAuthorHtml } from "@/lib/markdownTables";
import StudentLockedVideo from "@/components/student/StudentLockedVideo";
import StudentFileLink from "@/components/student/StudentFileLink";
import { isLockableVideoUrl, videoWatchKey, type WatchEntry, type WatchMap } from "@/lib/mediaWatch";

type WatchLock = {
  activityId: string;
  enabled: boolean;
  watch: WatchMap;
  onWatch: (key: string, entry: WatchEntry) => void;
};

/** Same stack the student activity page renders. */
export default function StudentBodyParts({
  parts,
  watchLock,
  activityId,
}: {
  parts: BodyPart[];
  watchLock?: WatchLock;
  activityId?: string;
}) {
  let lockableIndex = 0;
  return (
    <div className="student-body-parts">
      {parts.map((part, index) => {
        if (part.type === "html") {
          return <div key={index} dangerouslySetInnerHTML={{ __html: upgradeAuthorHtml(part.html) }} />;
        }
        if (part.type === "file") {
          return <StudentFileLink key={index} part={part} activityId={activityId} />;
        }
        if (part.type === "image") {
          return (
            <img
              key={index}
              src={part.url}
              alt={part.alt || ""}
              style={mediaBoxStyle(part)}
            />
          );
        }
        if (part.type === "audio") {
          return (
            <div key={index} className="student-body-audio-wrap">
              <audio className="student-body-audio" src={part.url} controls preload="metadata">
                Your browser cannot play this audio.
              </audio>
            </div>
          );
        }
        const play = videoPlayback(part.url);
        if (!play) return null;
        const box = mediaBoxStyle(part, { defaultRatio: "16 / 9" });
        if (play.kind === "file") {
          const key = videoWatchKey(part, lockableIndex);
          if (isLockableVideoUrl(part.url)) lockableIndex += 1;
          const player = watchLock?.enabled ? (
            <StudentLockedVideo
              part={part}
              watchKey={key}
              activityId={watchLock.activityId}
              locked
              initial={watchLock.watch[key]}
              onWatch={watchLock.onWatch}
            />
          ) : (
            <video className="student-body-video" src={play.src} controls style={box} />
          );
          return (
            <div key={index} className="student-body-video-wrap">
              {player}
            </div>
          );
        }
        return (
          <div key={index}>
            <div className="student-body-embed" style={box}>
              <iframe
                src={play.src}
                title="Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
